import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Menu, X, Home, Package, Calendar, Shield, RefreshCw, Activity, User, LogOut, Settings, ChevronDown, Sparkles, BookOpen, CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/common/ThemeToggle'
import { useAdminAuth } from '@/contexts/AdminAuthContext'
import { ReloadExperience } from '@/components/common/ReloadExperience'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export function Header() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAdminAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [showReloadExperience, setShowReloadExperience] = useState(false)
  const headerRef = useRef<HTMLElement>(null)

  // Auto-scroll to top when navigating between pages
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Handle scroll behavior for enhanced sticky effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      setIsScrolled(scrollTop > 10)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Handle reload with mind-blowing animation
  const handleReload = () => {
    setShowReloadExperience(true)
    // Reload after animation plays (2 seconds) - loader stays visible during and after reload
    setTimeout(() => {
      window.location.reload()
    }, 2000)
  }

  // Check if user is admin to show Solutions tab
  const isAdmin = isAuthenticated && (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN')

  const navigationItems = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'About', path: '/about', icon: BookOpen },
    { name: 'Demos', path: '/demos', icon: Package },
    { name: 'Events', path: '/events', icon: Sparkles },
    { name: 'Lab Status', path: '/lab-status', icon: Activity },
    { name: 'Booking', path: '/booking', icon: CalendarClock },
  ]

  const handleNavigation = (path: string) => {
    navigate(path)
    setIsMobileMenuOpen(false)
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/') // Redirect to main page instead of admin login
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  // Don't show header on admin pages or solution designer
  if (location.pathname.startsWith('/admin') || location.pathname === '/solutions/designer') {
    return null
  }

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-[100] w-full transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200/80 dark:border-gray-700/80 shadow-lg'
            : 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200/50 dark:border-gray-700/50'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="flex items-center cursor-pointer"
              onClick={() => handleNavigation('/')}
            >
              <>
                <img
                  src="/logo/new/WESTCOAST_dark.png"
                  alt="Westcoast HPE"
                  className="h-20 sm:h-24 w-auto block dark:hidden transition-all duration-200"
                />
                <img
                  src="/logo/new/WESTCOAST_light.png"
                  alt="Westcoast HPE"
                  className="h-20 sm:h-24 w-auto hidden dark:block transition-all duration-200"
                />
              </>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item) => {
                const Icon = item.icon
                const isActive = isActivePath(item.path)
                
                return (
                  <motion.div key={item.path} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleNavigation(item.path)}
                      className={`flex items-center gap-2 transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <Icon size={16} />
                      <span className="hidden lg:inline">{item.name}</span>
                    </Button>
                  </motion.div>
                )
              })}
            </nav>

            {/* Test Button, Admin Button, Theme Toggle and Mobile Menu */}
            <div className="flex items-center gap-2">
              {/* Test Loader Button - Desktop Only */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReload}
                className="hidden md:flex hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                title="Refresh Experience"
              >
                <RefreshCw size={18} />
              </Button>

              {/* Admin User Menu - Only show for authenticated admin users */}
              {isAuthenticated && (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                      title="User Menu"
                    >
                      <User size={18} />
                      <ChevronDown size={12} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel className="flex flex-col">
                      <span className="font-semibold">{user?.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {user?.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Admin'}
                      </span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Settings size={16} />
                      Admin Dashboard
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="flex items-center gap-2 cursor-pointer text-red-600 dark:text-red-400"
                    >
                      <LogOut size={16} />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              <ThemeToggle />
              
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-gray-200/50 dark:border-gray-700/50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg"
          >
            <nav className="container mx-auto px-4 py-3 space-y-1">
              {navigationItems.map((item) => {
                const Icon = item.icon
                const isActive = isActivePath(item.path)
                
                return (
                  <Button
                    key={item.path}
                    variant={isActive ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full justify-start gap-3 transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon size={18} />
                    {item.name}
                  </Button>
                )
              })}

              {/* Admin User Menu for Mobile - Only show for authenticated admin users */}
              {isAuthenticated && (user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                <>
                  <div className="border-t border-gray-200/50 dark:border-gray-700/50 my-2 pt-2">
                    <div className="px-2 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      User Menu
                    </div>
                  </div>
                  
                  <div className="px-2 py-1 mb-2">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      {user?.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {user?.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Admin'}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleNavigation('/admin')}
                    className="w-full justify-start gap-3 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <Settings size={18} />
                    Admin Dashboard
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="w-full justify-start gap-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <LogOut size={18} />
                    Sign Out
                  </Button>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </header>

      {/* Brand accent line */}
      <div className="fixed top-16 left-0 right-0 z-[99] h-1 bg-[#BBD134] opacity-30" />

      {/* Mind-Blowing Reload Experience */}
      <ReloadExperience isActive={showReloadExperience} />

      {/* TODO: Add SearchModal component */}
    </>
  )
}